<template>
  <div class="softphone-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader pageTitle="Softphone Settings" />
      <main class="page-content">

        <!-- Page Header -->
        <div class="page-header">
          <div class="header-content">
            <h1>Softphone Settings</h1>
            <p class="subtitle">Configure the FreePBX SIP server and transcription options for all agents.</p>
          </div>
          <div class="header-right">
            <button class="btn-secondary" @click="load" :disabled="loading">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg>
              Refresh
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
        </div>

        <!-- Error -->
        <div v-else-if="loadError" class="error-state">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
          <div>
            <div>{{ loadError }}</div>
            <button class="retry-btn" @click="load">Retry</button>
          </div>
        </div>

        <template v-else>

          <!-- ── Section 1: PBX & Transcription ─────────────────────── -->
          <div class="settings-card">
            <div class="card-header">
              <div class="card-header-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
              </div>
              <div>
                <h2>SIP / PBX Configuration</h2>
                <p>Organization-wide SIP server settings and call transcription options.</p>
              </div>
              <div v-if="!config.configured" class="unconfigured-badge">Not configured</div>
            </div>

            <div class="card-body">
              <!-- Save success / error -->
              <div v-if="saveSuccess" class="alert alert-success">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                Settings saved successfully.
              </div>
              <div v-if="saveError" class="alert alert-error">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                {{ saveError }}
              </div>

              <div class="form-grid">
                <!-- SIP Host -->
                <div class="form-group">
                  <label class="form-label">SIP Host</label>
                  <input
                    v-model="form.sip_host"
                    class="form-input"
                    placeholder="e.g. pbx.company.com or 192.168.1.10"
                    type="text"
                  />
                  <p class="form-hint">FreePBX server hostname or IP address.</p>
                </div>

                <!-- SIP Port -->
                <div class="form-group form-group-sm">
                  <label class="form-label">SIP Port</label>
                  <input
                    v-model.number="form.sip_port"
                    class="form-input"
                    type="number"
                    min="1"
                    max="65535"
                    placeholder="5060"
                  />
                  <p class="form-hint">Default: 5060</p>
                </div>
              </div>

              <div class="divider"></div>

              <!-- Transcription toggle -->
              <div class="toggle-row">
                <div class="toggle-label">
                  <span class="toggle-title">Real-time Transcription</span>
                  <span class="toggle-desc">Transcribe calls live as they happen for all agents.</span>
                </div>
                <label class="toggle-switch">
                  <input type="checkbox" v-model="form.use_transcription" />
                  <span class="toggle-track"></span>
                </label>
              </div>

              <div v-if="form.use_transcription" class="transcription-options">
                <div class="form-grid">
                  <!-- Language -->
                  <div class="form-group">
                    <label class="form-label">Language</label>
                    <select v-model="form.transcription_language" class="form-select">
                      <option value="ar">Arabic (ar)</option>
                      <option value="en">English (en)</option>
                      <option value="fr">French (fr)</option>
                      <option value="de">German (de)</option>
                    </select>
                    <p class="form-hint">BCP-47 language code used for Whisper.</p>
                  </div>

                  <!-- Model -->
                  <div class="form-group">
                    <label class="form-label">Transcription Model</label>
                    <select v-model="form.transcription_model" class="form-select">
                      <option value="whisper-large-v3-turbo">whisper-large-v3-turbo (default)</option>
                      <option value="qwen3-asr">qwen3-asr (Qwen3 ASR)</option>
                    </select>
                    <p class="form-hint">Larger models are more accurate but use more resources.</p>
                  </div>

                </div>

                <!-- Auto-analyze -->
                <div class="toggle-row">
                  <div class="toggle-label">
                    <span class="toggle-title">Auto-analyze Transcripts</span>
                    <span class="toggle-desc">Automatically analyze the transcript with AI after each call ends.</span>
                  </div>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="form.auto_analyze" />
                    <span class="toggle-track"></span>
                  </label>
                </div>
              </div>

              <div class="card-footer">
                <button class="btn-primary" @click="saveConfig" :disabled="saving">
                  <svg v-if="saving" class="spin-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 14.03 20 13.07 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/></svg>
                  {{ saving ? 'Saving…' : 'Save Settings' }}
                </button>
              </div>
            </div>
          </div>

          <!-- ── Section 2: Agent SIP Extensions ────────────────────── -->
          <div class="settings-card mt-4">
            <div class="card-header">
              <div class="card-header-icon card-header-icon--agents">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>
              </div>
              <div>
                <h2>Agent SIP Extensions</h2>
                <p>Assign FreePBX extension numbers and SIP passwords to individual agents.</p>
              </div>
              <div class="header-right-inline">
                <div class="search-wrap-sm" v-if="agents.length">
                  <svg class="search-icon-sm" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                  <input v-model="agentSearch" class="search-input-sm" placeholder="Search agents…" />
                </div>
              </div>
            </div>

            <div class="card-body p-0">
              <div v-if="agentsLoading" class="loading-state loading-state--inline">
                <div class="spinner spinner--sm"></div>
              </div>
              <div v-else-if="agentsError" class="error-state error-state--inline">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                {{ agentsError }}
              </div>
              <template v-else>
                <table class="data-table" v-if="filteredAgents.length">
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th>Extension</th>
                      <th>SIP Password</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="agent in filteredAgents" :key="agent.agent_id">
                      <!-- Agent info -->
                      <td>
                        <div class="agent-cell">
                          <div class="agent-avatar">{{ (agent.name || '?').charAt(0).toUpperCase() }}</div>
                          <div>
                            <div class="agent-name">{{ agent.name }}</div>
                            <div class="agent-code-badge">{{ agent.agent_code }}</div>
                          </div>
                        </div>
                      </td>
                      <!-- Extension -->
                      <td>
                        <span v-if="agent.extension" class="ext-number">{{ agent.extension }}</span>
                        <span v-else class="no-ext">Not assigned</span>
                      </td>
                      <!-- SIP Password -->
                      <td>
                        <span v-if="agent.sip_password" class="sip-pwd-mask">••••••••</span>
                        <span v-else class="no-ext">—</span>
                      </td>
                      <!-- Actions -->
                      <td class="action-cell">
                        <button class="btn-action btn-assign" @click="openAssign(agent)">
                          {{ agent.extension ? 'Edit' : 'Assign' }}
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div v-else-if="agentSearch" class="empty-state">
                  <h3>No results for "{{ agentSearch }}"</h3>
                </div>
                <div v-else class="empty-state">
                  <h3>No agents found</h3>
                  <p>Add agents in the Agents section first.</p>
                </div>
              </template>
            </div>
          </div>

        </template>
      </main>
    </div>

    <!-- ── Assign Extension Modal ──────────────────────────────────── -->
    <div v-if="showAssignModal" class="modal-overlay" @click.self="closeAssignModal">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ assignAgent && assignAgent.extension ? 'Edit Extension' : 'Assign Extension' }}</h2>
          <button class="modal-close" @click="closeAssignModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>
        </div>

        <div class="modal-body" v-if="assignAgent">
          <!-- Agent display -->
          <div class="agent-display">
            <div class="agent-display-avatar">{{ assignAgent.name.charAt(0).toUpperCase() }}</div>
            <div>
              <div class="agent-display-name">{{ assignAgent.name }}</div>
              <div class="agent-display-code">{{ assignAgent.agent_code }}</div>
            </div>
          </div>

          <!-- Error -->
          <div v-if="assignError" class="alert alert-error">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
            {{ assignError }}
          </div>

          <!-- Extension picker -->
          <div class="form-group">
            <label class="form-label">Extension #</label>
            <div class="ext-picker" v-if="extensions.length">
              <div class="ext-picker-search">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input v-model="extSearch" class="ext-picker-input" placeholder="Filter extensions…" />
              </div>
              <div class="ext-picker-list">
                <div v-if="filteredExtensions.length === 0" class="ext-picker-empty">No extensions found</div>
                <button
                  v-for="ext in filteredExtensions"
                  :key="ext.extension"
                  type="button"
                  class="ext-picker-item"
                  :class="{
                    'ext-item-selected': assignForm.extension === ext.extension,
                    'ext-item-disabled': isExtUsedByOther(ext.extension)
                  }"
                  :disabled="isExtUsedByOther(ext.extension)"
                  @click="assignForm.extension = ext.extension"
                >
                  <div class="ext-item-left">
                    <span class="ext-item-number">{{ ext.extension }}</span>
                    <span class="ext-item-name">{{ ext.display_name || ext.extension }}</span>
                  </div>
                  <div class="ext-item-right">
                    <span v-if="isExtUsedByOther(ext.extension)" class="ext-item-inuse">In use</span>
                    <span v-else-if="ext.type" class="ext-item-type">{{ ext.type.toUpperCase() }}</span>
                    <svg v-if="assignForm.extension === ext.extension" width="14" height="14" viewBox="0 0 24 24" fill="#3B82F6"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  </div>
                </button>
              </div>
            </div>
            <!-- Fallback: manual input if no extensions list available -->
            <input
              v-else
              v-model="assignForm.extension"
              class="form-input"
              type="text"
              placeholder="e.g. 3001"
            />
          </div>

          <!-- SIP Password -->
          <div class="form-group">
            <label class="form-label">
              SIP Password
              <span class="label-hint">{{ assignAgent.extension ? '(leave blank to keep existing)' : '' }}</span>
            </label>
            <div class="password-wrap">
              <input
                v-model="assignForm.sip_password"
                :type="showPwd ? 'text' : 'password'"
                class="form-input"
                :placeholder="assignAgent.extension ? '(saved — leave blank to keep)' : 'Enter SIP / extension password'"
              />
              <button class="show-pwd-btn" type="button" @click="showPwd = !showPwd">
                <svg v-if="!showPwd" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
                <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/></svg>
              </button>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-secondary" @click="closeAssignModal">Cancel</button>
          <button class="btn-primary" @click="saveAssign" :disabled="assigning || !assignForm.extension">
            <svg v-if="assigning" class="spin-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 14.03 20 13.07 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/></svg>
            {{ assigning ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { IVRService } from '@/services/ivrService'

export default {
  name: 'SoftphoneSettingsView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      // Page load
      loading: true,
      loadError: null,

      // Org config form
      config: {},
      form: {
        sip_host: '',
        sip_port: 5060,
        use_transcription: false,
        transcription_language: 'ar',
        transcription_model: 'whisper-large-v3-turbo',
        chunk_duration: 60,
        auto_analyze: false
      },
      saving: false,
      saveSuccess: false,
      saveError: null,

      // Agents table
      agents: [],
      agentsLoading: false,
      agentsError: null,
      agentSearch: '',

      // Extensions list (for picker)
      extensions: [],

      // Assign modal
      showAssignModal: false,
      assignAgent: null,
      assignForm: { extension: '', sip_password: '' },
      assigning: false,
      assignError: null,
      extSearch: '',
      showPwd: false
    }
  },

  computed: {
    filteredAgents () {
      if (!this.agentSearch) return this.agents
      const q = this.agentSearch.toLowerCase()
      return this.agents.filter(a =>
        (a.name || '').toLowerCase().includes(q) ||
        (a.agent_code || '').toLowerCase().includes(q) ||
        (a.extension || '').includes(q)
      )
    },

    filteredExtensions () {
      if (!this.extSearch) return this.extensions
      const q = this.extSearch.toLowerCase()
      return this.extensions.filter(e =>
        String(e.extension).includes(q) ||
        (e.display_name || '').toLowerCase().includes(q)
      )
    },

    // Map extension → agent for "in use" checks
    extensionMap () {
      const map = {}
      for (const a of this.agents) {
        if (a.extension) map[a.extension] = a
      }
      return map
    }
  },

  mounted () {
    this.load()
  },

  methods: {
    async load () {
      this.loading = true
      this.loadError = null
      try {
        const data = await IVRService.getSoftphoneConfig()
        this.config = data
        this.form.sip_host = data.sip_host || ''
        this.form.sip_port = data.sip_port || 5060
        this.form.use_transcription = !!data.use_transcription
        const allowedLangs = ['ar', 'en', 'fr', 'de']
        const allowedModels = ['whisper-large-v3-turbo', 'qwen3-asr']
        this.form.transcription_language = allowedLangs.includes(data.transcription_language) ? data.transcription_language : 'ar'
        this.form.transcription_model = allowedModels.includes(data.transcription_model) ? data.transcription_model : 'whisper-large-v3-turbo'
        this.form.chunk_duration = 60
        this.form.auto_analyze = !!data.auto_analyze
      } catch (e) {
        this.loadError = e.message || 'Failed to load softphone config'
      } finally {
        this.loading = false
      }
      // Load agents + extensions in parallel (non-fatal)
      this.loadAgents()
      this.loadExtensions()
    },

    async loadAgents () {
      this.agentsLoading = true
      this.agentsError = null
      try {
        const data = await IVRService.getAgentsExtensions()
        this.agents = data.agents || data || []
      } catch (e) {
        this.agentsError = e.message || 'Failed to load agents'
      } finally {
        this.agentsLoading = false
      }
    },

    async loadExtensions () {
      try {
        const data = await IVRService.getExtensions()
        this.extensions = data.extensions || data || []
      } catch {
        // Extensions list is optional — fall back to manual input
        this.extensions = []
      }
    },

    async saveConfig () {
      this.saving = true
      this.saveSuccess = false
      this.saveError = null
      try {
        const payload = {
          sip_host: this.form.sip_host || null,
          sip_port: this.form.sip_port || 5060,
          use_transcription: this.form.use_transcription,
          transcription_language: this.form.transcription_language,
          transcription_model: this.form.transcription_model,
          chunk_duration: this.form.chunk_duration,
          auto_analyze: this.form.auto_analyze
        }
        await IVRService.saveSoftphoneConfig(payload)
        this.saveSuccess = true
        this.config.configured = true
        setTimeout(() => { this.saveSuccess = false }, 3500)
      } catch (e) {
        this.saveError = e.message || 'Failed to save settings'
      } finally {
        this.saving = false
      }
    },

    openAssign (agent) {
      this.assignAgent = agent
      this.assignForm = {
        extension: agent.extension || '',
        sip_password: ''
      }
      this.assignError = null
      this.extSearch = ''
      this.showPwd = false
      this.showAssignModal = true
    },

    closeAssignModal () {
      this.showAssignModal = false
      this.assignAgent = null
      this.assignError = null
    },

    isExtUsedByOther (ext) {
      const occupant = this.extensionMap[ext]
      return occupant && occupant.agent_id !== this.assignAgent?.agent_id
    },

    async saveAssign () {
      if (!this.assignForm.extension) return
      this.assigning = true
      this.assignError = null
      try {
        const payload = {
          agent_id: this.assignAgent.agent_id,
          extension: this.assignForm.extension,
          sip_password: this.assignForm.sip_password || undefined
        }
        // Include org SIP host in case it's set
        if (this.form.sip_host) {
          payload.sip_host = this.form.sip_host
          payload.sip_port = this.form.sip_port || 5060
        }
        await IVRService.assignAgentSipConfig(payload)
        // Patch local agent list
        const idx = this.agents.findIndex(a => a.agent_id === this.assignAgent.agent_id)
        if (idx !== -1) {
          this.agents[idx] = {
            ...this.agents[idx],
            extension: this.assignForm.extension,
            sip_password: this.assignForm.sip_password || this.agents[idx].sip_password
          }
        }
        this.closeAssignModal()
      } catch (e) {
        this.assignError = e.message || 'Failed to assign extension'
      } finally {
        this.assigning = false
      }
    }
  }
}
</script>

<style scoped>
.softphone-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.page-content {
  flex: 1;
  padding: 24px;
  max-width: 900px;
}

/* ── Page Header ──────────────────────────────────────────── */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
}

.header-content h1 {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px;
}

.subtitle {
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

/* ── Loading / Error ─────────────────────────────────────── */
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 24px;
}

.loading-state--inline {
  padding: 32px;
}

.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--border-primary);
  border-top-color: #3B82F6;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.spinner--sm {
  width: 20px;
  height: 20px;
  border-width: 2px;
}

@keyframes spin { to { transform: rotate(360deg); } }

.error-state {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 20px 24px;
  background: var(--error-bg-light);
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  color: var(--error-color);
  font-size: 0.9rem;
  margin-bottom: 24px;
}

.error-state--inline {
  margin: 16px;
}

.retry-btn {
  margin-top: 6px;
  background: none;
  border: 1px solid currentColor;
  border-radius: 6px;
  padding: 4px 12px;
  cursor: pointer;
  color: inherit;
  font-size: 0.8rem;
}

/* ── Settings Card ───────────────────────────────────────── */
.settings-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  overflow: hidden;
}

.mt-4 { margin-top: 20px; }

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-secondary);
}

.card-header-icon {
  width: 38px;
  height: 38px;
  border-radius: 9px;
  background: #eff6ff;
  color: #3B82F6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-header-icon--agents {
  background: #f0fdf4;
  color: #16a34a;
}

.card-header h2 {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 3px;
}

.card-header p {
  font-size: 0.82rem;
  color: var(--text-secondary);
  margin: 0;
}

.unconfigured-badge {
  margin-left: auto;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.header-right-inline {
  margin-left: auto;
  flex-shrink: 0;
}

.card-body {
  padding: 24px;
}

.card-body.p-0 { padding: 0; }

.card-footer {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--border-primary);
  display: flex;
  justify-content: flex-end;
}

/* ── Alerts ──────────────────────────────────────────────── */
.alert {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-bottom: 20px;
}

.alert-success {
  background: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.alert-error {
  background: var(--error-bg-light);
  color: var(--error-color);
  border: 1px solid var(--border-primary);
}

/* ── Form Elements ───────────────────────────────────────── */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group-sm {
  grid-column: span 1;
}

.form-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 6px;
}

.label-hint {
  font-weight: 400;
  color: var(--text-tertiary);
  font-size: 0.78rem;
}

.form-input,
.form-select {
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--border-secondary);
  border-radius: 8px;
  font-size: 0.88rem;
  color: var(--text-primary);
  background: var(--bg-primary);
  transition: border-color 0.15s;
  outline: none;
  width: 100%;
  box-sizing: border-box;
}

.form-input:focus,
.form-select:focus {
  border-color: #3B82F6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-hint {
  font-size: 0.75rem;
  color: var(--text-tertiary);
  margin: 0;
}

.divider {
  height: 1px;
  background: var(--border-primary);
  margin: 20px 0;
}

/* ── Toggle switch ───────────────────────────────────────── */
.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
}

.toggle-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.toggle-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
}

.toggle-desc {
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  cursor: pointer;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.toggle-track {
  position: absolute;
  inset: 0;
  background: var(--border-secondary);
  border-radius: 24px;
  transition: background 0.2s;
}

.toggle-track::after {
  content: '';
  position: absolute;
  left: 3px;
  top: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  transition: transform 0.2s;
}

.toggle-switch input:checked + .toggle-track {
  background: #3B82F6;
}

.toggle-switch input:checked + .toggle-track::after {
  transform: translateX(20px);
}

.transcription-options {
  margin-top: 4px;
  padding: 16px 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 10px;
}

/* ── Buttons ─────────────────────────────────────────────── */
.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0 18px;
  height: 38px;
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-primary:hover:not(:disabled) { background: #2563EB; }
.btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0 14px;
  height: 36px;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-secondary:hover { background: var(--bg-secondary); }

.spin-icon {
  animation: spin 0.7s linear infinite;
}

/* ── Agent search (inline card header) ──────────────────── */
.search-wrap-sm {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 0 10px;
  height: 34px;
}

.search-icon-sm { color: var(--text-tertiary); flex-shrink: 0; }

.search-input-sm {
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.83rem;
  color: var(--text-primary);
  width: 160px;
}

/* ── Data table ──────────────────────────────────────────── */
.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  padding: 10px 16px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-secondary);
  text-align: left;
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-primary);
}

.data-table td {
  padding: 12px 16px;
  font-size: 0.88rem;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-primary);
  vertical-align: middle;
}

.data-table tr:last-child td { border-bottom: none; }

.agent-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.agent-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3B82F6, #6366F1);
  color: white;
  font-weight: 700;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.agent-name {
  font-weight: 600;
  font-size: 0.88rem;
  color: var(--text-primary);
}

.agent-code-badge {
  font-size: 0.72rem;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  border-radius: 4px;
  padding: 1px 6px;
  display: inline-block;
  margin-top: 2px;
}

.ext-number {
  font-family: monospace;
  font-size: 0.9rem;
  font-weight: 700;
  color: #3B82F6;
}

.sip-pwd-mask {
  font-size: 0.9rem;
  color: var(--text-tertiary);
  letter-spacing: 2px;
}

.no-ext {
  font-size: 0.82rem;
  color: var(--text-tertiary);
}

.action-cell { text-align: right; }

.btn-action {
  padding: 5px 14px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background 0.15s;
}

.btn-assign {
  background: #eff6ff;
  color: #3B82F6;
}

.btn-assign:hover { background: #dbeafe; }

.empty-state {
  padding: 48px 24px;
  text-align: center;
  color: var(--text-secondary);
}

.empty-state h3 {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 6px;
}

.empty-state p {
  font-size: 0.83rem;
  margin: 0;
}

/* ── Modal ───────────────────────────────────────────────── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal {
  background: var(--bg-primary);
  border-radius: 14px;
  width: 100%;
  max-width: 480px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid var(--border-primary);
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover { background: var(--bg-tertiary); }

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid var(--border-primary);
  flex-shrink: 0;
}

.agent-display {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 10px;
}

.agent-display-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3B82F6, #6366F1);
  color: white;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}

.agent-display-name {
  font-weight: 700;
  font-size: 0.93rem;
  color: var(--text-primary);
}

.agent-display-code {
  font-size: 0.78rem;
  color: var(--text-secondary);
}

/* ── Extension picker ────────────────────────────────────── */
.ext-picker {
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  overflow: hidden;
}

.ext-picker-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-secondary);
}

.ext-picker-search svg { color: var(--text-tertiary); flex-shrink: 0; }

.ext-picker-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.85rem;
  color: var(--text-primary);
}

.ext-picker-list {
  max-height: 200px;
  overflow-y: auto;
}

.ext-picker-empty {
  padding: 14px;
  font-size: 0.83rem;
  color: var(--text-tertiary);
  text-align: center;
}

.ext-picker-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 9px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;
  border-bottom: 1px solid var(--border-primary);
}

.ext-picker-item:last-child { border-bottom: none; }
.ext-picker-item:hover:not(.ext-item-disabled) { background: var(--bg-secondary); }

.ext-item-selected {
  background: #eff6ff !important;
}

.ext-item-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.ext-item-left {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.ext-item-number {
  font-family: monospace;
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--text-primary);
}

.ext-item-name {
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.ext-item-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ext-item-type {
  font-size: 0.68rem;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border-radius: 4px;
  padding: 1px 5px;
  font-weight: 600;
}

.ext-item-inuse {
  font-size: 0.72rem;
  color: #b45309;
  background: #fef3c7;
  border-radius: 4px;
  padding: 1px 6px;
  font-weight: 600;
}

/* ── Password wrap ───────────────────────────────────────── */
.password-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.password-wrap .form-input {
  padding-right: 40px;
}

.show-pwd-btn {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-tertiary);
  padding: 0;
  display: flex;
  align-items: center;
}

.show-pwd-btn:hover { color: var(--text-primary); }
</style>
